<template>
    <div class="w-full">
        <label v-if="label" :for="id" class="block text-sm text-gray-900">{{
            label
        }}</label>
        <div
            class="relative rounded-lg"
            :class="[addOnRight || addOnLeft ? 'flex' : '', label ? 'mt-1' : '']"
        >
            <textarea
                v-if="textarea"
                :name="name"
                :id="id"
                :rows="textAreaRows"
                class="block w-full pr-10 text-sm rounded-lg focus:outline-none disabled:bg-gray-200"
                :class="
                    error
                        ? 'text-red-900 border-red-300 placeholder-red-300 focus:border-red-500 ring-red-500'
                        : 'text-gray-900 border-gray-300 placeholder-gray-500 focus:border-gray-900 ring-gray-900'
                "
                :placeholder="placeholder"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                :disabled="disabled"
                :maxlength="maxlength"
            />

            <span
                v-if="addOnLeft"
                class="inline-flex items-center px-3 m-0 text-sm text-gray-500 border border-r-0 border-gray-300 rounded-l-md"
                :class="disabled ? 'bg-gray-200' : 'bg-gray-50'"
                >{{ addOnText }}</span
            >

            <input
                v-if="!textarea"
                :type="type"
                :name="name"
                :id="id"
                class="block w-full text-sm rounded-lg h-11 disabled:bg-gray-200 focus:outline-none"
                :class="[
                    customClass,
                    error ? addOnLeft ? 'rounded-none rounded-r-lg' : '' : 'rounded-lg',
                    error ? addOnRight ? 'rounded-none rounded-l-lg' : '' : 'rounded-lg',
                    error 
                        ? 'text-red-900 border-red-300 placeholder-red-300 focus:border-red-500 ring-red-500'
                        : addOnLeft
                        ? 'text-gray-800 border-gray-300 placeholder-gray-500 flex-1 min-w-0 rounded-none rounded-r-lg focus:border-gray-900 ring-gray-900'
                        : addOnRight
                        ? 'text-gray-900 border-gray-300 placeholder-gray-500 flex-1 min-w-0 rounded-none rounded-l-lg focus:border-gray-900 ring-gray-900'
                        : 'text-gray-900 border-gray-300 placeholder-gray-500 focus:border-gray-900 ring-gray-900'
                ]"
                :placeholder="placeholder"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                :disabled="disabled"
                :min="min"
                :max="max"
                :step="step"
                :maxlength="maxlength"
                :oninput="oninput"
                :autocomplete="autocomplete"
            />

            <slot name="password"/>

            <span
                v-if="addOnRight"
                class="inline-flex items-center px-3 m-0 text-sm text-gray-500 border border-l-0 border-gray-300 rounded-r-md"
                :class="disabled ? 'bg-gray-200' : 'bg-gray-50'"
                >{{ addOnText }}</span
            >
            <div
                v-if="error"
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none "
            >
                <ExclamationCircleIcon
                    class="w-5 h-5 text-red-500"
                    aria-hidden="true"
                />
            </div>
        </div>

        <small
            class="block mt-1 text-right"
            :class="[
                modelValue.length >= maxlength
                    ? 'text-red-600'
                    : 'text-gray-400',
            ]"
            v-if="showInputLimit"
        >
            {{ modelValue.length }}/{{ maxlength }}
        </small>

        <p 
            v-if="helper"
            class="text-[10px] text-gray-600 mt-1"
        >
            {{ helper }}
        </p>

        <p
            v-if="error"
            class="text-[10px] mt-1"
            :class="error ? 'text-red-600' : 'text-gray-800'"
            id="email-error"
        >
            {{ error }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { ExclamationCircleIcon } from "@heroicons/vue/24/solid";
import { watch } from "vue";

const props = defineProps({
    modelValue: {
        type: String,
        default: null,
        required: true,
    },
    helper: {
        type: String,
        default: null,
    },
    error: {
        type: String,
        default: null,
    },
    placeholder: {
        type: String,
        default: null,
    },
    autocomplete: {
        type: String,
        default: 'off',
    },
    name: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    type: {
        type: String,
        default: "text",
    },
    textarea: {
        type: Boolean,
        default: false,
    },
    textAreaRows: {
        type: Number,
        default: 10,
    },
    addOnRight: {
        type: Boolean,
        default: false,
    },
    addOnText: {
        type: String,
        default: null,
    },
    oninput: {
        type: String,
        default: null,
    },
    addOnLeft: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    min: {
        type: Number,
        default: 0,
    },
    max: {
        type: Number,
    },
    step: {
        type: Number,
        default: 1,
    },
    maxlength: {
        type: Number,
    },
    showInputLimit: {
        type: Boolean,
        default: false,
    },
    customClass: {
        type: String,
        default: "",
    },
});


const emit = defineEmits(['update:modelValue'])

watch(
    () => props.modelValue,
    (value:number) => {
        if(props.type == 'number' && props.min){
            
            if(value < props.min){
                // props.modelValue = props.min
                emit('update:modelValue', props.min)
            }
        }
    }
)
</script>